import React, { useState } from 'react';
import { Card, Button, Modal } from 'react-bootstrap';
import { API_BASE_URL, WEB_BASE_URL } from '../../config';
import QRCode from 'qrcode';

interface NetworkLevel {
  level: number;
  totalUsers: number;
  activeUsers: number;
}

interface ProfileCardProps {
  userName: string;
  photo: string;
  isLoading: boolean;
  userParent: string;
  emailParent: string;
  referralCode: string;
  networkLevels?: NetworkLevel[]; // Hacer opcional
  onCopyLink: () => void;
  onShareWhatsApp: () => void;
  onShareEmail: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
  userName,
  photo,
  isLoading,
  userParent,
  emailParent,
  referralCode,
  networkLevels,
  onCopyLink,
  onShareWhatsApp,
  onShareEmail
}) => {
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  const generateQRCode = async () => {
    try {
      const referralLink = `${WEB_BASE_URL}/sign-up?code=${referralCode}`;
      const qrCodeDataURL = await QRCode.toDataURL(referralLink, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrCodeDataURL);
      setQrModalOpen(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Error al generar el código QR');
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;

    const link = document.createElement('a');
    link.download = `qr-code-${referralCode}.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyReferralLink = () => {
    const referralLink = `${WEB_BASE_URL}/sign-up?code=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    alert('¡Enlace copiado al portapapeles!');
  };
  return (
    <Card className="border-0 rounded-2 p-4 bg-white mb-4">
      <div className="d-flex align-items-center mb-4">
        <div className="flex-shrink-0 position-relative">
          <img
            src={photo ? `${API_BASE_URL}/users/show/photo/${photo}` : '/images/logo-icon.svg'}
            className="rounded-circle"
            alt="User Avatar"
            width={65}
            height={65}
          />
        </div>
        <div className="flex-grow-1 ms-3">
          <h4 className="fs-24 fw-semibold mb-1">{userName}</h4>
          <span className="fs-12">Auspiciante: {userParent}({emailParent}) </span>
        </div>
      </div>

      {/* Referral Code Section */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div className="d-flex align-items-center">
          <div className="flex-shrink-0 position-relative">
            <img
              src="/images/share_profile.svg"
              alt="Share profile icon"
              width={30}
              height={30}
              style={{ fill: "red" }}
            />
          </div>
          <div className="flex-grow-1 ms-3">
            <span className="fs-12">Código de Referido</span>
            <h4 className="fs-14 fw-semibold mb-0">{referralCode}</h4>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            className="btn btn-sm btn-outline-primary"
            onClick={onCopyLink}
            title="Copiar enlace"
          >
            <i className="ri-file-copy-line"></i>
          </Button>
          <Button
            className="btn btn-sm btn-outline-primary"
            onClick={onShareWhatsApp}
            title="Compartir por WhatsApp"
          >
            <i className="ri-whatsapp-line"></i>
          </Button>
          <Button
            className="btn btn-sm btn-outline-primary"
            onClick={onShareEmail}
            title="Compartir por Email"
          >
            <i className="ri-mail-line"></i>
          </Button>
          <Button
            className="btn btn-sm btn-outline-primary"
            onClick={generateQRCode}
            title="Generar código QR"
          >
            <i className="ri-qr-code-line"></i>
          </Button>
        </div>
      </div>

        {/* Network Levels Statistics */}
      <h5 className="fs-16 mb-3">Niveles de Red</h5>
      {isLoading ? (
        <span>Cargando...</span>
      ) : networkLevels?.map((level) => (
        <div 
          key={level.level} 
          className="d-flex flex-wrap justify-content-between align-items-center mb-3 p-2 border-bottom"
        >
          <div>
            <span className="fw-medium">Nivel {level.level}</span>
          </div>
          <div className="d-flex gap-3">
            <div className="text-center">
              <span className="fs-12 d-block mb-1 text-body">Total</span>
              <span className="badge bg-light text-black">{level.totalUsers}</span>
            </div>
            <div className="text-center">
              <span className="fs-12 d-block mb-1 text-body">Activos</span>
              <span className="badge bg-primary">{level.activeUsers}</span>
            </div>
          </div>
        </div>
      ))}


      {/* QR Code Modal */}
      <Modal show={qrModalOpen} onHide={() => setQrModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Código QR de Referido</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <h6>Código: {referralCode}</h6>
              <small className="text-muted">Escanea este código QR para registrarse con tu código de referido</small>
            </div>
            {qrCodeUrl && (
              <div className="mb-3">
                <img
                  src={qrCodeUrl}
                  alt="QR Code"
                  className="img-fluid"
                  style={{ maxWidth: '256px', border: '1px solid #dee2e6', borderRadius: '8px' }}
                />
              </div>
            )}
            <div className="d-flex justify-content-center gap-2">
              <Button
                variant="primary"
                onClick={downloadQRCode}
                disabled={!qrCodeUrl}
              >
                <i className="ri-download-line me-1"></i>
                Descargar QR
              </Button>
              <Button
                variant="outline-secondary"
                onClick={copyReferralLink}
              >
                <i className="ri-file-copy-line me-1"></i>
                Copiar Enlace
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default ProfileCard;
